import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';

const fmt = (d) => d ? new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';
const displayTime = (time) => {
  if (!time) return '';
  const [hh, mm] = String(time).split(':');
  let h = Number(hh);
  if (Number.isNaN(h)) return time;
  const suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${mm || '00'} ${suffix}`;
};
const timeText = (a) => a?.exact_time ? displayTime(a.exact_time) : String(a?.time_preference || 'exact').replace(/_/g, ' ');
const statusLabel = (s) => String(s || 'draft').replace(/_/g, ' ');
const typeLabel = (a) => a?.appointment_type_label || 'Appointment';
const providerLabel = (a) => a?.provider_type_label || typeLabel(a) || 'Provider';
const providerName = (a) => a?.farrier_name || providerLabel(a);

const FarrierOwnerRespondPage = () => {
  const { token } = useParams();
  const [appt, setAppt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/farrier/owner/${token}`);
      setAppt(data);
    } catch (e) {
      toast.error('Could not load appointment');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const serviceCount = useMemo(() => (appt?.service_groups || []).reduce((sum, g) => sum + (g.horses || []).length, 0), [appt]);

  const acceptSuggested = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/farrier/owner/${token}/accept-suggestion`, {});
      setAppt(data);
      toast.success('Alternate time accepted. Appointment added to Connected Rider calendar.');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to accept alternate time');
    } finally {
      setBusy(false);
    }
  };

  const declineSuggested = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/farrier/owner/${token}/decline-suggestion`, { reason: declineReason });
      setAppt(data);
      toast.success('Alternate time declined. Farrier has been notified.');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to decline alternate time');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!appt) return <div className="page-content"><div className="empty-state"><h3>Appointment not found</h3></div></div>;

  const hasSuggestedTime = appt.suggested_date && appt.suggested_exact_time;
  const isAccepted = appt.status === 'accepted';

  return (
    <div>
      <div className="top-bar">
        <h1 style={{ fontSize: 18 }}>{typeLabel(appt)} alternate time</h1>
        <p style={{ opacity: 0.85, fontSize: 12, marginTop: 2 }}>Connected Rider</p>
      </div>
      <div className="page-content">
        <div className="card" style={{ padding: '14px' }}>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Current requested appointment</div>
          <div style={{ fontSize: 19, fontWeight: 800, marginTop: 4 }}>{fmt(appt.proposed_date)} at {timeText(appt)}</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6 }}>{providerLabel(appt)}: {providerName(appt)}</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>Total horse services: {serviceCount}</div>
          <div style={{ marginTop: 10 }}><span className="badge badge-amber">{statusLabel(appt.status)}</span></div>
        </div>

        {hasSuggestedTime && (
          <div className="card" style={{ padding: '14px', border: '2px solid var(--primary-color)' }}>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Farrier suggested</div>
            <div style={{ fontSize: 22, fontWeight: 900, marginTop: 4 }}>{fmt(appt.suggested_date)} at {displayTime(appt.suggested_exact_time)}</div>
            {appt.suggested_notes && <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8, marginBottom: 0 }}>{appt.suggested_notes}</p>}
            {!isAccepted && (
              <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
                <button className="btn btn-primary btn-full" onClick={acceptSuggested} disabled={busy}>
                  {busy ? 'Accepting...' : 'Accept suggested time'}
                </button>
                <div>
                  <label className="form-label">Optional note if declining</label>
                  <textarea className="form-input" rows="2" value={declineReason} onChange={e => setDeclineReason(e.target.value)} placeholder="Example: That time will not work, please suggest another." />
                </div>
                <button className="btn btn-secondary btn-full" onClick={declineSuggested} disabled={busy}>
                  Decline suggested time
                </button>
              </div>
            )}
          </div>
        )}

        {isAccepted && (
          <>
            <div className="card" style={{ padding: '14px' }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Appointment confirmed</div>
              <p style={{ marginTop: 6, color: 'var(--text-secondary)', fontSize: 13 }}>
                This appointment was added to the Connected Rider calendar. The provider has been sent a confirmation text with calendar options.
              </p>
            </div>
            <div className="section-label">Add to your calendar</div>
            <div className="card" style={{ display: 'grid', gap: 8 }}>
              <a className="btn btn-primary btn-full" href={`/api/farrier/owner/${token}/google-calendar`} target="_blank" rel="noreferrer">Add to Google Calendar</a>
              <a className="btn btn-secondary btn-full" href={`/api/farrier/owner/${token}/calendar.ics`}>Add to Apple / Outlook Calendar</a>
              <a className="btn btn-secondary btn-full" href={`/api/farrier/owner/${token}/calendar.ics`}>Download calendar file</a>
            </div>
          </>
        )}

        <div className="section-label">Services</div>
        {(appt.service_groups || []).map(g => (
          <div key={g.id} className="card" style={{ padding: '12px 14px' }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{g.service_label} — {(g.horses || []).length} horse{(g.horses || []).length === 1 ? '' : 's'}</div>
            {g.notes && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{g.notes}</div>}
            <div style={{ marginTop: 8 }}>
              {(g.horses || []).map(h => (
                <div key={h.id} style={{ padding: '6px 0', borderTop: '1px solid var(--border-color)' }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{h.horse_name_snapshot}</div>
                  {h.horse_notes && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{h.horse_notes}</div>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FarrierOwnerRespondPage;
